import React, { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import LoadingSpinner from '../../components/LoadingSpinner';

const AdminLocales = () => {
  const [locales, setLocales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocal, setEditingLocal] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '', descripcion: '', categoria: '', slug: '', telefono: '',
    email: '', whatsapp: '', productos: '', servicios: '', keywords: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchLocales();
  }, []);

  const fetchLocales = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'locales'));
      setLocales(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      toast.error('Error al cargar locales');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      let imagenPrincipal = editingLocal?.imagenPrincipal || '';
      if (imageFile) {
        const imageRef = ref(storage, `locales/${Date.now()}_${imageFile.name}`);
        await uploadBytes(imageRef, imageFile);
        imagenPrincipal = await getDownloadURL(imageRef);
      }

      const localData = {
        ...formData,
        imagenPrincipal,
        keywords: formData.keywords.split(',').map(k => k.trim()),
        visitas: editingLocal?.visitas || 0
      };

      if (editingLocal) {
        await updateDoc(doc(db, 'locales', editingLocal.id), localData);
        toast.success('Local actualizado');
      } else {
        await addDoc(collection(db, 'locales'), localData);
        toast.success('Local creado');
      }

      setDialogOpen(false);
      resetForm();
      fetchLocales();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (local) => {
    if (!window.confirm('¿Eliminar este local?')) return;
    try {
      await deleteDoc(doc(db, 'locales', local.id));
      toast.success('Local eliminado');
      fetchLocales();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleEdit = (local) => {
    setEditingLocal(local);
    setFormData({
      nombre: local.nombre, descripcion: local.descripcion,
      categoria: local.categoria, slug: local.slug,
      telefono: local.telefono || '', email: local.email || '',
      whatsapp: local.whatsapp || '', productos: local.productos || '',
      servicios: local.servicios || '',
      keywords: (local.keywords || []).join(', ')
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingLocal(null);
    setFormData({ nombre: '', descripcion: '', categoria: '', slug: '', telefono: '', email: '', whatsapp: '', productos: '', servicios: '', keywords: '' });
    setImageFile(null);
  };

  if (loading) return <LoadingSpinner fullScreen />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-heading font-bold">Gestión de Locales</h1>
          <p className="text-muted-foreground">Administra los locales comerciales</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button><Plus className="mr-2" size={18} />Nuevo Local</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingLocal ? 'Editar' : 'Nuevo'} Local</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre *</Label>
                  <Input value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Categoría *</Label>
                  <Input value={formData.categoria} onChange={(e) => setFormData({ ...formData, categoria: e.target.value })} required />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Slug (URL) *</Label>
                <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Descripción *</Label>
                <Textarea value={formData.descripcion} onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>WhatsApp</Label>
                <Input value={formData.whatsapp} onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })} placeholder="573001234567" />
              </div>
              <div className="space-y-2">
                <Label>Productos</Label>
                <Textarea value={formData.productos} onChange={(e) => setFormData({ ...formData, productos: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Servicios</Label>
                <Textarea value={formData.servicios} onChange={(e) => setFormData({ ...formData, servicios: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Keywords (separadas por comas)</Label>
                <Input value={formData.keywords} onChange={(e) => setFormData({ ...formData, keywords: e.target.value })} placeholder="moda, ropa, accesorios" />
              </div>
              <div className="space-y-2">
                <Label>Imagen {!editingLocal && '*'}</Label>
                <Input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} required={!editingLocal} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancelar</Button>
                <Button type="submit" disabled={uploading}>{uploading ? 'Guardando...' : 'Guardar'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {locales.map((local) => (
          <Card key={local.id}>
            <div className="relative h-48">
              <img src={local.imagenPrincipal} alt={local.nombre} className="w-full h-full object-cover" />
            </div>
            <CardContent className="p-4">
              <h3 className="font-heading font-semibold mb-1">{local.nombre}</h3>
              <p className="text-sm text-muted-foreground mb-2">{local.categoria}</p>
              <p className="text-xs text-muted-foreground mb-4">Visitas: {local.visitas || 0}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleEdit(local)} className="flex-1">
                  <Pencil size={14} className="mr-1" />Editar
                </Button>
                <Button variant="destructive" size="sm" onClick={() => handleDelete(local)} className="flex-1">
                  <Trash2 size={14} className="mr-1" />Eliminar
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminLocales;
